import React, { useState, useEffect } from 'react';
import { Shield, Settings, Users, Eye, Grid3x3, ChevronDown, ChevronRight, Loader2, Check, LayoutGrid } from 'lucide-react';
import type { Role, Permission } from '../../services/security/roles.service';
import { RolesService } from '../../services/security/roles.service';
import { httpClient } from '../../services/api/http-client';
import { ConfirmationToast } from '../ui/ConfirmationToast';
import { NotificationToast } from '../ui/NotificationToast';

interface RoleDetailsDrawerProps {
  isOpen: boolean;
  role: Role | null;
  onClose: () => void;
  onEditRole: (role: Role) => void;
  onToggleRoleStatus: (role: Role) => Promise<void>;
}

export const RoleDetailsDrawer: React.FC<RoleDetailsDrawerProps> = ({
  isOpen,
  role,
  onClose,
  onEditRole,
  onToggleRoleStatus
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentRole, setCurrentRole] = useState<Role | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'modules'>('info');
  
  // Estados para confirmación y loading
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<'toggle' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Estados para notificaciones
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  // Estados para manejo de permisos por módulo
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [modulePermissions, setModulePermissions] = useState<Map<string, Permission[]>>(new Map());
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [loadingModulePermissions, setLoadingModulePermissions] = useState<Set<string>>(new Set());
  const [loadingPermissionAction, setLoadingPermissionAction] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && role) {
      setCurrentRole(role);
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
    }
  }, [isOpen, role]);

  useEffect(() => {
    if (!isVisible && !isOpen) {
      const timer = setTimeout(() => {
        setCurrentRole(null);
        setExpandedModules(new Set());
        setModulePermissions(new Map());
        setAllPermissions([]);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible, isOpen]);

  // Cargar todos los permisos disponibles cuando se abre la pestaña de módulos
  useEffect(() => {
    if (activeTab === 'modules' && isOpen && currentRole) {
      loadAllPermissions();
      // Cargar permisos de todos los módulos para mostrar el contador
      loadAllModulesPermissions();
    }
  }, [activeTab, isOpen, currentRole]);

  const loadAllModulesPermissions = async () => {
    if (!currentRole?.modulos) return;
    
    const promises = currentRole.modulos.map(async (module) => {
      try {
        const response = await httpClient.get<any>(
          `/auth/roles/${currentRole.id}/modules/${module.id}/permissions`
        );
        const responseData = response.data?.data || response.data;
        const assignedPermissions = responseData?.permissions || [];
        
        
        return {
          moduleId: module.id,
          permissions: assignedPermissions
        };
      } catch (error) {
        console.error(`Error loading permissions for module ${module.nombre}:`, error);
        return {
          moduleId: module.id,
          permissions: []
        };
      }
    });

    try {
      const results = await Promise.all(promises);
      const newModulePermissions = new Map(modulePermissions);
      
      results.forEach(({ moduleId, permissions }) => {
        newModulePermissions.set(moduleId, permissions);
      });
      
      setModulePermissions(newModulePermissions);
    } catch (error) {
      console.error('Error loading all modules permissions:', error);
    }
  };

  const loadAllPermissions = async () => {
    try {
      const permissions = await RolesService.getAllPermissions();
      
      // Si permissions viene como {data: Array}, extraemos el array
      const permissionsArray = Array.isArray(permissions) ? permissions : (permissions as any)?.data || [];
      
      setAllPermissions(permissionsArray);
    } catch (error) {
      console.error('Error loading permissions:', error);
      showErrorNotification('Error al cargar permisos disponibles');
    }
  };

  const toggleModuleExpansion = async (moduleId: string) => {
    
    const newExpandedModules = new Set(expandedModules);
    
    if (expandedModules.has(moduleId)) {
      newExpandedModules.delete(moduleId);
    } else {
      newExpandedModules.add(moduleId);
      // Cargar permisos si no los tenemos aún
      if (!modulePermissions.has(moduleId)) {
        await loadModulePermissions(moduleId);
      } else {
        // FORZAR recarga para debug
        await loadModulePermissions(moduleId);
      }
    }
    
    setExpandedModules(newExpandedModules);
  };

  const loadModulePermissions = async (moduleId: string) => {
    if (!currentRole) return;
    
    setLoadingModulePermissions(prev => new Set(prev).add(moduleId));
    
    try {
      // Usar httpClient que maneja automáticamente la URL base y auth
      const response = await httpClient.get<any>(
        `/auth/roles/${currentRole.id}/modules/${moduleId}/permissions`
      );
      
      const responseData = response.data?.data || response.data;
      
      const assignedPermissions = responseData?.permissions || [];
      const allAvailablePermissions = responseData?.allPermissions || [];
      
      
      setModulePermissions(prev => new Map(prev).set(moduleId, assignedPermissions));
      
      // Si no tenemos todos los permisos cargados, los cargamos ahora
      if (allPermissions.length === 0) {
        setAllPermissions(allAvailablePermissions);
      }
    } catch (error) {
      console.error('Error loading module permissions:', error);
      showErrorNotification('Error al cargar permisos del módulo');
    } finally {
      setLoadingModulePermissions(prev => {
        const newSet = new Set(prev);
        newSet.delete(moduleId);
        return newSet;
      });
    }
  };

  const handlePermissionToggle = async (moduleId: string, permission: Permission, isCurrentlyAssigned: boolean) => {
    if (!currentRole) return;
    
    
    const actionKey = `${moduleId}-${permission.id}`;
    setLoadingPermissionAction(actionKey);
    
    try {
      if (isCurrentlyAssigned) {
        await RolesService.removePermissionFromRoleModule(currentRole.id, moduleId, permission.id);
        showSuccessNotification(`Permiso "${permission.nombre}" removido del módulo`);
      } else {
        await RolesService.addPermissionToRoleModule(currentRole.id, moduleId, permission.id);
        showSuccessNotification(`Permiso "${permission.nombre}" agregado al módulo`);
      }
      
      // Recargar permisos del módulo
      await loadModulePermissions(moduleId);
    } catch (error) {
      console.error('Error toggling permission:', error);
      showErrorNotification('Error al gestionar permiso');
    } finally {
      setLoadingPermissionAction(null);
    }
  };

  const showSuccessNotification = (message: string) => {
    setNotificationConfig({
      title: '¡Éxito!',
      message,
      type: 'success'
    });
    setShowNotification(true);
  };

  const showErrorNotification = (message: string) => {
    setNotificationConfig({
      title: 'Error',
      message,
      type: 'error'
    });
    setShowNotification(true);
  };

  const handleClose = () => {
    setIsVisible(false);
    onClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (activo: boolean) => {
    return activo 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const handleEditRole = () => {
    if (currentRole) {
      onEditRole(currentRole);
    }
  };

  const handleToggleRoleStatus = () => {
    setConfirmationAction('toggle');
    setShowConfirmation(true);
  };

  const handleConfirmAction = async () => {
    if (!currentRole || !confirmationAction) return;
    
    setIsLoading(true);
    try {
      if (confirmationAction === 'toggle') {
        await onToggleRoleStatus(currentRole);
        
        setNotificationConfig({
          title: '¡Éxito!',
          message: `Rol ${currentRole.activo ? 'desactivado' : 'activado'} correctamente`,
          type: 'success'
        });
        setShowNotification(true);
      }
    } catch (error) {
      console.error('Error al ejecutar acción:', error);
      
      setNotificationConfig({
        title: 'Error',
        message: 'No se pudo cambiar el estado del rol. Inténtalo nuevamente.',
        type: 'error'
      });
      setShowNotification(true);
    } finally {
      setIsLoading(false);
      setShowConfirmation(false);
      setConfirmationAction(null);
    }
  };

  const handleCancelAction = () => {
    setShowConfirmation(false);
    setConfirmationAction(null);
  };

  const getConfirmationConfig = () => {
    if (!currentRole || !confirmationAction) return null;
    
    if (confirmationAction === 'toggle') {
      return {
        title: currentRole.activo ? 'Desactivar Rol' : 'Activar Rol',
        message: currentRole.activo 
          ? `¿Estás seguro de que deseas desactivar el rol "${currentRole.nombre}"? Los usuarios con este rol perderán los permisos asociados.`
          : `¿Estás seguro de que deseas activar el rol "${currentRole.nombre}"? Los usuarios con este rol recuperarán los permisos asociados.`,
        confirmText: currentRole.activo ? 'Desactivar' : 'Activar',
        type: currentRole.activo ? 'danger' as const : 'success' as const
      };
    }
    
    return null;
  };

  const getModuleIcon = (iconName: string) => {
    const icons: { [key: string]: React.ComponentType<any> } = {
      'settings': Settings,
      'users': Users,
      'shield': Shield,
      'eye': Eye,
      'grid': Grid3x3
    };
    
    return icons[iconName] || Settings;
  };

  if (!isOpen && !currentRole) return null;
  if (!currentRole) return null;

  return (
    <div className="fixed inset-0 z-20 overflow-hidden" style={{ top: '90px' }}>
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={handleClose}
          aria-label="Cerrar drawer"
        />
        
        <section className={`absolute right-0 top-0 h-full w-full max-w-6xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div 
            className="h-full flex flex-col bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-8 py-5 bg-gradient-to-r from-purple-600 to-indigo-600 border-b border-purple-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      Detalles del Rol
                    </h2>
                    <p className="text-sm text-purple-100 mt-0.5">
                      {currentRole.nombre}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="text-white/70 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8">
              {/* Header del rol */}
              <div className="flex items-center gap-5 mb-8 pb-6 border-b border-gray-100">
                
                <div className="flex-1 min-w-0">
                  <h3 className="text-2xl font-bold text-gray-900">
                    {currentRole.nombre}
                  </h3>
                  {currentRole.descripcion && (
                    <p className="text-gray-600 mt-1">{currentRole.descripcion}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2">
                    <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(currentRole.activo)}`}>
                      {currentRole.activo ? 'Activo' : 'Inactivo'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {currentRole.modulos?.length || 0} módulo(s)
                    </span>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="px-6 border-b border-gray-200">
                <div className="flex space-x-8">
                  <button
                    onClick={() => setActiveTab('info')}
                    className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'info'
                        ? 'text-purple-600 border-purple-600'
                        : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Información General
                  </button>
                  <button
                    onClick={() => setActiveTab('modules')}
                    className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'modules'
                        ? 'text-purple-600 border-purple-600'
                        : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Módulos y Permisos
                  </button>
                </div>
              </div>

              {/* Contenido de las pestañas */}
              <div>
                {activeTab === 'info' && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Información básica */}
                    <div className="lg:col-span-2">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Settings className="w-5 h-5 text-purple-500" />
                        Información Básica
                      </h4>
                      <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Nombre del Rol</label>
                            <p className="text-gray-900 font-medium text-lg">{currentRole.nombre}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Estado</label>
                            <div className="flex items-center gap-2">
                              <div className={`w-2.5 h-2.5 rounded-full ${currentRole.activo ? 'bg-green-500' : 'bg-red-500'}`}></div>
                              <p className="text-gray-900 font-medium">{currentRole.activo ? 'Activo' : 'Inactivo'}</p>
                            </div>
                          </div>
                        </div>
                        {currentRole.descripcion && (
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Descripción</label>
                            <p className="text-gray-900">{currentRole.descripcion}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Información del Sistema */}
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Shield className="w-5 h-5 text-purple-500" />
                        Información del Sistema
                      </h4>
                      <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 space-y-3 text-sm text-gray-600">
                        <div className="flex justify-between">
                          <span className="font-medium">ID</span>
                          <span className="font-mono text-gray-500">{currentRole.id}</span>
                        </div>
                        <div className="h-px bg-gray-200"></div>
                        <div className="flex justify-between">
                          <span className="font-medium">Creado</span>
                          <span>{formatDate(currentRole.creadoEn)}</span>
                        </div>
                        <div className="h-px bg-gray-200"></div>
                        <div className="flex justify-between">
                          <span className="font-medium">Actualizado</span>
                          <span>{formatDate(currentRole.actualizadoEn)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'modules' && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <LayoutGrid className="w-5 h-5 text-purple-500" />
                        Módulos y Permisos Asignados
                      </h4>
                      <span className="text-sm font-medium text-purple-700 bg-purple-50 px-3 py-1 rounded-full">
                        {currentRole.modulos?.length || 0} módulo(s)
                      </span>
                    </div>
                    
                    {currentRole.modulos && currentRole.modulos.length > 0 ? (
                      <div className="space-y-4">
                        {currentRole.modulos.map((module) => {
                          const IconComponent = getModuleIcon(module.icono);
                          const isExpanded = expandedModules.has(module.id);
                          const isLoadingPermissions = loadingModulePermissions.has(module.id);
                          const assignedPermissions = modulePermissions.get(module.id) || [];
                          
                          return (
                            <div key={module.id} className="border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                              {/* Header del módulo - clickeable */}
                              <div 
                                className="p-5 cursor-pointer hover:bg-gray-50 transition-colors"
                                onClick={() => toggleModuleExpansion(module.id)}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-4">
                                    <div className="p-3 bg-purple-100 rounded-xl">
                                      <IconComponent className="w-6 h-6 text-purple-600" />
                                    </div>
                                    <div>
                                      <h5 className="font-semibold text-gray-900 text-lg flex items-center">
                                        {module.nombre}
                                        {isExpanded ? (
                                          <ChevronDown className="w-4 h-4 ml-2 text-gray-500" />
                                        ) : (
                                          <ChevronRight className="w-4 h-4 ml-2 text-gray-500" />
                                        )}
                                      </h5>
                                      {module.descripcion && (
                                        <p className="text-sm text-gray-600 mt-1">{module.descripcion}</p>
                                      )}
                                      <div className="flex items-center gap-2 mt-2">
                                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                          module.activo 
                                            ? 'bg-green-100 text-green-800' 
                                            : 'bg-gray-100 text-gray-800'
                                        }`}>
                                          {module.activo ? 'Activo' : 'Inactivo'}
                                        </span>
                                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                                          assignedPermissions.length > 0 
                                            ? 'bg-blue-100 text-blue-800' 
                                            : 'bg-gray-100 text-gray-600'
                                        }`}>
                                          {assignedPermissions.length}/{allPermissions.length} permisos
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Gestión de permisos expandible */}
                              {isExpanded && (
                                <div className="border-t border-gray-200 p-5 bg-gray-50/50">
                                  {isLoadingPermissions ? (
                                    <div className="flex items-center justify-center py-8">
                                      <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                                      <span className="ml-2 text-gray-600">Cargando permisos...</span>
                                    </div>
                                  ) : (
                                    <div>
                                      <h6 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                        <Settings className="w-4 h-4 text-purple-500" />
                                        Gestionar Permisos
                                      </h6>
                                      
                                      {(allPermissions && allPermissions.length > 0) ? (
                                        <div>
                                          <p className="text-xs text-gray-500 mb-4">
                                            Haz clic en un permiso para activarlo o desactivarlo
                                          </p>
                                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                            {allPermissions.map((permission: Permission) => {
                                              const isAssigned = assignedPermissions.some(ap => ap.id === permission.id);
                                              const isLoadingAction = loadingPermissionAction === `${module.id}-${permission.id}`;
                                              
                                              return (
                                                <button
                                                  key={permission.id}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handlePermissionToggle(module.id, permission, isAssigned);
                                                  }}
                                                  disabled={isLoadingAction || !permission.activo}
                                                  className={`relative flex items-start gap-3 p-4 rounded-xl border text-left transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                                                    isAssigned
                                                      ? 'bg-green-50 border-green-400 hover:bg-green-100 hover:shadow-sm'
                                                      : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                                                  }`}
                                                  title={permission.descripcion}
                                                >
                                                  <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                    isAssigned ? 'bg-green-500 text-white' : 'border-2 border-gray-300'
                                                  }`}>
                                                    {isAssigned && <Check className="w-3 h-3" />}
                                                  </div>
                                                  <div className="flex-1 min-w-0">
                                                    <p className={`text-sm font-medium ${isAssigned ? 'text-green-900' : 'text-gray-700'}`}>
                                                      {permission.nombre}
                                                    </p>
                                                    {permission.descripcion && (
                                                      <p className={`text-xs mt-0.5 truncate ${isAssigned ? 'text-green-700' : 'text-gray-500'}`}>
                                                        {permission.descripcion}
                                                      </p>
                                                    )}
                                                  </div>
                                                  {isLoadingAction && (
                                                    <Loader2 className="w-4 h-4 animate-spin text-purple-600 absolute top-3 right-3" />
                                                  )}
                                                </button>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="text-center py-4 text-gray-500">
                                          <p className="text-sm">No hay permisos disponibles para gestionar</p>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-16 text-gray-500 border border-dashed border-gray-200 rounded-xl">
                        <Shield className="mx-auto h-14 w-14 text-gray-300 mb-3" />
                        <p className="text-lg font-medium mb-1">Sin módulos asignados</p>
                        <p className="text-sm text-gray-400">Este rol no tiene ningún módulo configurado.</p>
                        <p className="text-sm text-gray-400">Edita el rol para asignar permisos.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer con acciones */}
            <div className="px-8 py-5 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <button 
                  onClick={handleToggleRoleStatus}
                  disabled={isLoading}
                  className={`px-6 py-2.5 rounded-lg transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                    currentRole.activo 
                      ? 'bg-white border border-red-300 text-red-600 hover:bg-red-50' 
                      : 'bg-white border border-green-300 text-green-600 hover:bg-green-50'
                  }`}
                >
                  {currentRole.activo ? 'Desactivar Rol' : 'Activar Rol'}
                </button>
                <button 
                  onClick={handleEditRole}
                  className="px-8 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-all font-medium text-sm shadow-md"
                >
                  Editar Rol
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
      
      {/* Confirmation Toast */}
      {(() => {
        const config = getConfirmationConfig();
        return config ? (
          <ConfirmationToast
            isOpen={showConfirmation}
            title={config.title}
            message={config.message}
            confirmText={config.confirmText}
            type={config.type}
            onConfirm={handleConfirmAction}
            onCancel={handleCancelAction}
            isLoading={isLoading}
          />
        ) : null;
      })()}
      
      {/* Notification Toast */}
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => {
            setShowNotification(false);
            setNotificationConfig(null);
          }}
          duration={7000}
        />
      )}
    </div>
  );
};

export default RoleDetailsDrawer;